import { useMutation, useQueryClient, useQuery, queryOptions, mutationOptions } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import type { CreateTenantPayload, EditTenantPayload } from "./tenants.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/org/tenants`;
const BASE_ENDPOINT_FORMS_ENDPOINT_ADMIN = `${FORMS_API_BASE_ENDPOINT}/admin/tenants`;

const tenantKeys = {
  all: () => ["tenants"] as const,
  hierarchy: () => [...tenantKeys.all(), "hierarchy"] as const,
  connectedEntities: () => [...tenantKeys.all(), "connectedEntities"] as const,
  connectedEntitiesById: (tenantId: string) => [...tenantKeys.all(), "connectedEntitiesById", tenantId] as const,
};

/** Create tenant  */
export const useCreateTenant = () => {
  const queryClient = useQueryClient();
  const createTenant = (payload: CreateTenantPayload) => api.post(BASE_ENDPOINT, payload);

  return useMutation({
    mutationFn: createTenant,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Edit tenant  */
export const useEditTenant = () => {
  const queryClient = useQueryClient();
  const editTenant = ({ tenantId, payload }: { tenantId: string; payload: EditTenantPayload }) =>
    api.patch(`${BASE_ENDPOINT_FORMS_ENDPOINT_ADMIN}/${tenantId}`, payload);

  return useMutation({
    mutationFn: editTenant,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

export const deleteTenantMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT_FORMS_ENDPOINT_ADMIN}/${id}`),
    onSuccess: (_, id) => invalidateAll({ ignore: [tenantKeys.connectedEntitiesById(id)] }),
  });

const connectedEntityCounts = {
  registrations: z.number(),
  processorRegistrations: z.number(),
  dataProtectionImpactAssessments: z.number(),
  preAssessments: z.number(),
  partners: z.number(),
};

export const tenantConnectedEntitiesQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: tenantKeys.connectedEntitiesById(tenantId),
    retry: 1,
    queryFn: () =>
      api.get(`${FORMS_API_BASE_ENDPOINT}/tenant/${tenantId}/deleteImpact`, {
        schema: z.object({
          subTenants: z.number(),
          sharedRegistrations: z.number(),
          registrationsLosingDpiaConnection: z.number(),
          ...connectedEntityCounts,
          tenants: z.array(z.object({ tenantId: z.string(), ...connectedEntityCounts })),
        }),
      }),
  });

export type GetTenantHierarchyListResponse = {
  id: string;
  organizationName: string;
  parentId?: string | null;
  children: GetTenantHierarchyListResponse[];
};

const GetTenantsHierarchyListResponseSchema: z.ZodType<GetTenantHierarchyListResponse> = z.lazy(() =>
  z.object({
    id: z.string(),
    organizationName: z.string(),
    parentId: z.string().nullish(),
    children: z.array(GetTenantsHierarchyListResponseSchema),
  })
);

const getTenantsHierarchyListQueryOptions = (options: Pick<UseQueryOptions, "enabled"> = { enabled: true }) => {
  return queryOptions({
    queryKey: tenantKeys.hierarchy(),
    queryFn: () =>
      api.get(`${FORMS_API_BASE_ENDPOINT}/organisations/hierarchy-list`, {
        schema: GetTenantsHierarchyListResponseSchema,
      }),
    throwOnError: true,
    enabled: Boolean(options.enabled),
  });
};

export const useGetTenantsHierarchyList = (options: Pick<UseQueryOptions, "enabled"> = { enabled: true }) =>
  useQuery(getTenantsHierarchyListQueryOptions(options));

/** Get connected entities for tenants */
export const useGetHasConnectedEntitiesMap = () => {
  return useQuery({
    queryKey: tenantKeys.connectedEntities(),
    queryFn: async () => {
      const data = await api.get(`${FORMS_API_BASE_ENDPOINT}/tenant/connectedentities`, {
        schema: z.array(
          z.object({
            tenantId: z.string(),
            hasConnectedEntities: z.boolean(),
          })
        ),
      });
      return new Map(data.map(item => [item.tenantId, item.hasConnectedEntities]));
    },
    throwOnError: true,
  });
};
